/**
 * Country reference data shared by the public renderer (phone country-code +
 * address country dropdowns) and the admin (the global enable/disable list).
 * Which countries appear, and the default, are configured globally — see the
 * `country_config` setting and `PublicForm.disabledCountries` / `defaultCountry`.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code (the stable id used by the disabled list). */
  iso2: string;
  name: string;
  /** International dialling code, e.g. "+44". */
  dial: string;
}

/** Default country (UK) — phone code defaults to +44. */
export const DEFAULT_COUNTRY_ISO = 'GB';

/** Regional-indicator flag emoji for an ISO alpha-2 code. */
export function flagEmoji(iso2: string): string {
  const cc = iso2.toUpperCase();
  if (cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
}

/** Countries minus the globally-disabled ISO codes. */
export function enabledCountries(disabled: string[] = []): Country[] {
  const off = new Set(disabled.map((c) => c.toUpperCase()));
  const on = COUNTRIES.filter((c) => !off.has(c.iso2));
  // United Kingdom first (Hayden, 23/10/2026), then the rest in order.
  return [...on.filter((c) => c.iso2 === 'GB'), ...on.filter((c) => c.iso2 !== 'GB')];
}

/** A country list (iQ's, as value/label) with the United Kingdom moved to the top. */
export function ukFirst<T extends { value: string; label: string }>(options: T[]): T[] {
  const isUk = (o: T) => ['GB', 'UK'].includes(o.value.toUpperCase()) || /^united kingdom\b/i.test(o.label.trim());
  const uk = options.filter(isUk);
  return uk.length ? [...uk, ...options.filter((o) => !isUk(o))] : options;
}

export function countryByIso(iso2: string | undefined): Country | undefined {
  if (!iso2) return undefined;
  const cc = iso2.toUpperCase();
  return COUNTRIES.find((c) => c.iso2 === cc);
}

/** Match a stored phone string to its country by the longest dial-code prefix. */
export function countryByDialPrefix(value: string): Country | undefined {
  const v = value.replace(/\s+/g, '');
  let best: Country | undefined;
  for (const c of COUNTRIES) {
    if (v.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) best = c;
  }
  return best;
}

export const COUNTRIES: Country[] = [
  { iso2: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso2: 'IE', name: 'Ireland', dial: '+353' },
  { iso2: 'US', name: 'United States', dial: '+1' },
  { iso2: 'CA', name: 'Canada', dial: '+1' },
  { iso2: 'AU', name: 'Australia', dial: '+61' },
  { iso2: 'NZ', name: 'New Zealand', dial: '+64' },
  { iso2: 'FR', name: 'France', dial: '+33' },
  { iso2: 'DE', name: 'Germany', dial: '+49' },
  { iso2: 'ES', name: 'Spain', dial: '+34' },
  { iso2: 'PT', name: 'Portugal', dial: '+351' },
  { iso2: 'IT', name: 'Italy', dial: '+39' },
  { iso2: 'NL', name: 'Netherlands', dial: '+31' },
  { iso2: 'BE', name: 'Belgium', dial: '+32' },
  { iso2: 'LU', name: 'Luxembourg', dial: '+352' },
  { iso2: 'CH', name: 'Switzerland', dial: '+41' },
  { iso2: 'AT', name: 'Austria', dial: '+43' },
  { iso2: 'DK', name: 'Denmark', dial: '+45' },
  { iso2: 'SE', name: 'Sweden', dial: '+46' },
  { iso2: 'NO', name: 'Norway', dial: '+47' },
  { iso2: 'FI', name: 'Finland', dial: '+358' },
  { iso2: 'IS', name: 'Iceland', dial: '+354' },
  { iso2: 'PL', name: 'Poland', dial: '+48' },
  { iso2: 'CZ', name: 'Czechia', dial: '+420' },
  { iso2: 'SK', name: 'Slovakia', dial: '+421' },
  { iso2: 'HU', name: 'Hungary', dial: '+36' },
  { iso2: 'RO', name: 'Romania', dial: '+40' },
  { iso2: 'BG', name: 'Bulgaria', dial: '+359' },
  { iso2: 'GR', name: 'Greece', dial: '+30' },
  { iso2: 'HR', name: 'Croatia', dial: '+385' },
  { iso2: 'SI', name: 'Slovenia', dial: '+386' },
  { iso2: 'RS', name: 'Serbia', dial: '+381' },
  { iso2: 'EE', name: 'Estonia', dial: '+372' },
  { iso2: 'LV', name: 'Latvia', dial: '+371' },
  { iso2: 'LT', name: 'Lithuania', dial: '+370' },
  { iso2: 'UA', name: 'Ukraine', dial: '+380' },
  { iso2: 'TR', name: 'Turkey', dial: '+90' },
  { iso2: 'CY', name: 'Cyprus', dial: '+357' },
  { iso2: 'MT', name: 'Malta', dial: '+356' },
  { iso2: 'MX', name: 'Mexico', dial: '+52' },
  { iso2: 'BR', name: 'Brazil', dial: '+55' },
  { iso2: 'AR', name: 'Argentina', dial: '+54' },
  { iso2: 'CL', name: 'Chile', dial: '+56' },
  { iso2: 'CO', name: 'Colombia', dial: '+57' },
  { iso2: 'ZA', name: 'South Africa', dial: '+27' },
  { iso2: 'NG', name: 'Nigeria', dial: '+234' },
  { iso2: 'KE', name: 'Kenya', dial: '+254' },
  { iso2: 'GH', name: 'Ghana', dial: '+233' },
  { iso2: 'EG', name: 'Egypt', dial: '+20' },
  { iso2: 'MA', name: 'Morocco', dial: '+212' },
  { iso2: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { iso2: 'SA', name: 'Saudi Arabia', dial: '+966' },
  { iso2: 'QA', name: 'Qatar', dial: '+974' },
  { iso2: 'IL', name: 'Israel', dial: '+972' },
  { iso2: 'IN', name: 'India', dial: '+91' },
  { iso2: 'PK', name: 'Pakistan', dial: '+92' },
  { iso2: 'CN', name: 'China', dial: '+86' },
  { iso2: 'HK', name: 'Hong Kong', dial: '+852' },
  { iso2: 'SG', name: 'Singapore', dial: '+65' },
  { iso2: 'MY', name: 'Malaysia', dial: '+60' },
  { iso2: 'TH', name: 'Thailand', dial: '+66' },
  { iso2: 'ID', name: 'Indonesia', dial: '+62' },
  { iso2: 'PH', name: 'Philippines', dial: '+63' },
  { iso2: 'VN', name: 'Vietnam', dial: '+84' },
  { iso2: 'JP', name: 'Japan', dial: '+81' },
  { iso2: 'KR', name: 'South Korea', dial: '+82' },
  { iso2: 'RU', name: 'Russia', dial: '+7' },
];
